/**
 * The trash: the space's deleted items (most recently deleted first), as they were, with who deleted them and when.
 * Laid out like the items list: a header with a quiet summary and (admins) a menu to clean up at once, a toolbar to
 * search and filter (a bar for the selection instead while anything is selected), and rows that restore with one
 * button and keep deleting for good in their menu.
 */
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type {
  Job,
  PageTrashRow,
  Purged,
  RestoreOut,
  SpaceOut,
  TrashFilter,
  TrashItems,
  TrashRow,
  TrashSize,
} from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import type { Action } from "../lib/actions.ts";
import { useEntities } from "../lib/data.ts";
import { formatBytes } from "../lib/format.ts";
import { KINDS } from "../lib/itemQuery.ts";
import { sayDone, TRASH_JOBS, useJobs } from "../lib/jobs.ts";
import { useMedia } from "../lib/media.ts";
import { spaceApi } from "../lib/paths.ts";
import { useAction, useResource } from "../lib/resource.ts";
import { atLeast, canEdit } from "../lib/roles.ts";
import { location, setQuery } from "../lib/router.ts";
import { SELECTED_ROW, type Selection, selectedCount, useSelection } from "../lib/selection.ts";
import type { PageProps } from "../lib/state.ts";
import { useValueText } from "../lib/textState.ts";
import { toast } from "../lib/toast.ts";
import { Button } from "../ui/Button.tsx";
import { ConfirmDialog } from "../ui/Dialog.tsx";
import { Input, Select } from "../ui/form.tsx";
import { CleanupIcon, FolderIcon, MoreIcon, RestoreIcon, SearchIcon, ShredIcon, TrashIcon } from "../ui/icons.tsx";
import { JobProgress } from "../ui/jobProgress.tsx";
import { Menu } from "../ui/Menu.tsx";
import { Async, EmptyState } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { Paged } from "../ui/Pager.tsx";
import { SelectAll, SelectBox, SelectionBar, type Shown } from "../ui/selection.tsx";
import { iconButtonClass } from "../ui/styles.ts";
import { Table, Td, Th } from "../ui/Table.tsx";
import { Toolbar } from "../ui/Toolbar.tsx";
import { EntityLabel } from "./items/entityLabel.tsx";
import { KindIcon } from "./items/icons.tsx";
import { CleanupDialog, type CleanupMode } from "./trash/cleanup.tsx";
import { DeletedBy } from "./trash/deletedBy.tsx";
import { RestoreReport } from "./trash/report.tsx";
import { typeCount } from "./trash/typeCount.ts";
import { TrashFolderFilter, TrashPlace } from "./trash/where.tsx";

const PAGE_SIZE = 50;

/** The trash's search, from the page's URL: words, kind, entity, and a folder (with its subfolders, when asked). */
const filterOf = (sp: URLSearchParams): TrashFilter => ({
  q: sp.get("q"),
  kind: sp.get("kind"),
  entity: sp.get("entity"),
  ...(sp.get("folder") ? { folder: sp.get("folder"), deep: sp.get("deep") === "true" ? true : null } : {}),
});
/** How long typing pauses before the list follows the search. */
const SEARCH_MS = 300;

/** "3 items · 11.8 kB", and apart (explained on hover) what history keeps of items still there. */
function Summary({ size }: { size: TrashSize }) {
  return (
    <>
      <span>{t("trash.summary", { count: size.items, size: formatBytes(size.bytes) })}</span>
      {size.old_versions > 0 ? (
        <span class="ml-2 text-zinc-400" title={t("trash.oldVersionsHint")}>
          {t("trash.oldVersions", { size: formatBytes(size.old_versions) })}
        </span>
      ) : null}
    </>
  );
}

/** The header's actions, in its "⋯": cleaning up at once (admins). */
function cleanupActions(onPick: (mode: CleanupMode) => void): Action[] {
  return [
    {
      key: "cleanupOlder",
      label: t("trash.cleanupOlder"),
      icon: <CleanupIcon />,
      danger: true,
      overflow: true,
      onSelect: () => onPick("older"),
    },
    {
      key: "emptyTrash",
      label: t("trash.emptyTrash"),
      icon: <ShredIcon />,
      symbol: "trash",
      danger: true,
      overflow: true,
      onSelect: () => onPick("all"),
    },
  ];
}

/** The title search: the list follows as you type (after a pause), and the field follows the list (back, a link). */
function SearchField() {
  const asked = location.value.searchParams.get("q") ?? "";
  const [q, setQ] = useValueText(
    asked,
    (v) => v,
    (text, v) => text === v,
  );
  useEffect(() => {
    if (q.trim() === asked) {
      return;
    }
    const timer = setTimeout(() => setQuery({ q: q.trim() || null, cursor: null }), SEARCH_MS);
    return () => clearTimeout(timer);
  }, [q, asked]);
  return (
    <div class="relative min-w-48 flex-1 sm:max-w-sm">
      <span class="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400">
        <SearchIcon size={16} />
      </span>
      <Input
        type="search"
        class="pl-8"
        aria-label={t("trash.search")}
        placeholder={t("trash.search")}
        value={q}
        onValue={setQ}
      />
    </div>
  );
}

/**
 * Search, kind and entity, together on the left (as the items list's filters); what's selected acts on the right.
 * Narrow, the kind and the entity go into its "⋯" (the entity first).
 */
function SearchBar({
  space,
  rows,
  children,
}: {
  space: SpaceOut;
  /** The page's rows (naming a folder picked from one). */
  rows: readonly TrashRow[];
  children?: ComponentChildren;
}) {
  const sp = location.value.searchParams;
  const entities = useEntities(space);
  return (
    <search class="mb-4 flex">
      <Toolbar
        items={[
          { key: "search", node: <SearchField />, class: "flex-1 sm:max-w-sm", min: SEARCH_MIN_PX },
          {
            key: "kind",
            node: (
              <div class="w-40 shrink-0">
                <Select
                  aria-label={t("items.kind")}
                  value={sp.get("kind") ?? ""}
                  onValue={(v) => setQuery({ kind: v || null, cursor: null })}
                  options={[
                    { value: "", label: t("items.anyKind") },
                    ...KINDS.map((k) => ({ value: k, label: t(`items.kindFilter.${k}`) })),
                  ]}
                />
              </div>
            ),
            drop: 10,
          },
          {
            key: "entity",
            node: (
              <div class="w-40 shrink-0">
                <Select
                  aria-label={t("items.entity")}
                  value={sp.get("entity") ?? ""}
                  onValue={(v) => setQuery({ entity: v || null, cursor: null })}
                  options={[
                    { value: "", label: t("trash.anyEntity") },
                    ...(entities.data ?? []).map((e) => ({ value: e.id, label: e.name })),
                    { value: "none", label: t("items.noEntity") },
                  ]}
                />
              </div>
            ),
            drop: 20,
          },
          { key: "folder", node: <TrashFolderFilter space={space} rows={rows} />, drop: 15 },
          { key: "selection", node: children, shrinks: true, end: true },
        ]}
      />
    </search>
  );
}

/** The least the search field takes (its `min-w-48`). */
const SEARCH_MIN_PX = 192;

/** Items of the trash to act on (by id, or every one a search of it matches), and how many. */
interface Chosen {
  items: TrashItems;
  count: number;
}

interface Env {
  space: SpaceOut;
  /** Whether rows can be selected (there's something to do with them). */
  selectable: boolean;
  sel: Selection;
  /** What the list shows (once it's loaded), and its search as a bulk action's `filter`. */
  shown: Shown | undefined;
  filter: TrashFilter;
  restore: (items: TrashItems) => void;
  purge: (chosen: Chosen) => void;
  busy: boolean;
}

/** The selection's items: every match (the list's search), or those picked. */
function chosen(env: Env): Chosen {
  const count = selectedCount(env.sel, env.shown?.total);
  return env.sel.all ? { items: { filter: env.filter }, count } : { items: { ids: [...env.sel.ids] }, count };
}

/** "2 selected · Restore · Delete permanently · Clear selection", at the toolbar's end (searching still works). */
function SelectionActions({ env }: { env: Env }) {
  return (
    <SelectionBar
      sel={env.sel}
      shown={env.shown}
      items={[
        ...(canEdit(env.space)
          ? [
              {
                key: "restore",
                node: (
                  <Button size="sm" busy={env.busy} onClick={() => env.restore(chosen(env).items)}>
                    <RestoreIcon size={14} />
                    {t("trash.restore")}
                  </Button>
                ),
                drop: 20,
                menu: [
                  {
                    label: t("trash.restore"),
                    icon: <RestoreIcon size={14} />,
                    onSelect: () => env.busy || env.restore(chosen(env).items),
                  },
                ],
              },
            ]
          : []),
        ...(atLeast(env.space.role, "admin")
          ? [
              {
                key: "purge",
                node: (
                  <Button size="sm" variant="danger" disabled={env.busy} onClick={() => env.purge(chosen(env))}>
                    <ShredIcon />
                    {t("trash.purge")}
                  </Button>
                ),
                drop: 10,
                menu: [
                  {
                    label: t("trash.purge"),
                    icon: <ShredIcon />,
                    danger: true,
                    onSelect: () => env.busy || env.purge(chosen(env)),
                  },
                ],
              },
            ]
          : []),
      ]}
    />
  );
}

/** A row: the item (selectable) as it was, when and by whom it was deleted, its file's size, and what to do. */
function Row({ env, row }: { env: Env; row: TrashRow }) {
  const { space } = env;
  const entity = useEntities(space).data?.find((e) => e.id === row.entity);
  const title = row.title || t("common.untitled");
  const admin = atLeast(space.role, "admin");
  return (
    <tr class={env.sel.has(row.id) ? SELECTED_ROW : undefined}>
      <Td wide={true} class="align-middle!">
        <span class="flex items-start gap-3">
          {env.selectable ? <SelectBox sel={env.sel} id={row.id} title={title} class="mt-0.5" /> : null}
          {row.object === "folder" ? <FolderIcon size={20} class="shrink-0 text-zinc-500" /> : <KindIcon item={row} />}
          <span class="min-w-0">
            <span class="block text-zinc-900 [overflow-wrap:anywhere]">{title}</span>
            {entity || row.attachments > 0 || row.folder_path.length > 0 || row.object === "folder" ? (
              <span class="mt-0.5 flex flex-wrap items-center gap-2 text-xs font-normal text-zinc-500">
                {entity ? <EntityLabel entity={entity} /> : null}
                {row.attachments > 0 ? <span>{t("trash.attachments", { count: row.attachments })}</span> : null}
                <TrashPlace space={space} row={row} />
              </span>
            ) : null}
          </span>
        </span>
      </Td>
      <Td label={t("trash.deleted")} class="align-middle!">
        <DeletedBy row={row} />
      </Td>
      <Td
        label={t("trash.size")}
        align="right"
        class={`align-middle! text-zinc-600 tabular-nums${row.file ? "" : " max-sm:hidden!"}`}
      >
        {row.file ? formatBytes(row.file.size) : "—"}
      </Td>
      <Td label="" class="align-middle! whitespace-nowrap sm:w-px">
        <span class="flex items-center justify-end gap-1">
          {canEdit(space) ? (
            <Button size="sm" disabled={env.busy} onClick={() => env.restore({ ids: [row.id] })}>
              <RestoreIcon size={14} />
              {t("trash.restore")}
            </Button>
          ) : null}
          {admin ? (
            <Menu
              label={
                <>
                  <MoreIcon size={16} />
                  <span class="sr-only">{t("trash.more", { title })}</span>
                </>
              }
              class={iconButtonClass("sm", "ghost")}
              items={[
                {
                  label: t("trash.purge"),
                  icon: <ShredIcon />,
                  danger: true,
                  onSelect: () => env.purge({ items: { ids: [row.id] }, count: 1 }),
                },
              ]}
            />
          ) : null}
        </span>
      </Td>
    </tr>
  );
}

/** The page's rows under a head whose checkbox selects (or clears) them all. */
function Rows({ env, page, onGo }: { env: Env; page: PageTrashRow; onGo: (cursor: string | null) => void }) {
  const ids = page.data.map((r) => r.id);
  return (
    <Paged page={page} size={PAGE_SIZE} go={onGo}>
      <Table stack={true}>
        <thead class="bg-zinc-50">
          <tr>
            <Th>
              <span class="flex items-center gap-3">
                {env.selectable ? <SelectAll sel={env.sel} ids={ids} /> : null}
                {t("trash.item")}
              </span>
            </Th>
            <Th>{t("trash.deleted")}</Th>
            <Th align="right">{t("trash.size")}</Th>
            <Th>
              <span class="sr-only">{t("trash.rowActions")}</span>
            </Th>
          </tr>
        </thead>
        <tbody class="divide-y divide-zinc-100">
          {page.data.map((row) => (
            <Row key={row.id} env={env} row={row} />
          ))}
        </tbody>
      </Table>
    </Paged>
  );
}

/** The list, or what to say when there's nothing in it: an empty trash, or nothing matching. */
function Listing({ env, page }: { env: Env; page: PageTrashRow }) {
  const sp = location.value.searchParams;
  if (page.data.length > 0) {
    return (
      <>
        <Rows env={env} page={page} onGo={(cursor) => setQuery({ cursor })} />
      </>
    );
  }
  if (sp.get("q") || sp.get("kind") || sp.get("entity") || sp.get("folder")) {
    return <EmptyState title={t("common.noMatches")} />;
  }
  return (
    <EmptyState title={t("trash.empty")} icon={<TrashIcon size={32} />}>
      {t("trash.emptyHint")}
    </EmptyState>
  );
}

export function Trash({ space }: PageProps) {
  const sp = location.value.searchParams;
  const list = useResource(`trash:${space.id}?${sp.toString()}`, (signal) =>
    api.get<PageTrashRow>(
      spaceApi(space, "/trash"),
      { ...filterOf(sp), cursor: sp.get("cursor"), limit: PAGE_SIZE },
      signal,
    ),
  );
  const size = useResource(`trash-size:${space.id}`, (signal) =>
    api.get<TrashSize>(spaceApi(space, "/trash/size"), {}, signal),
  );
  // The search, as restoring or purging every item it matches takes it.
  const filter = filterOf(sp);
  // What's selected stays so across pages and searches (every match: until the search changes).
  const sel = useSelection(space.id, JSON.stringify(filter));
  const [report, setReport] = useState<RestoreOut | null>(null);
  const [purging, setPurging] = useState<Chosen | null>(null);
  const [cleanup, setCleanup] = useState<CleanupMode | null>(null);
  const titles = new Map((list.data?.data ?? []).map((r) => [r.id, r.title]));
  // Narrow (a phone): the selection's bar on its own line, not squeezing the search.
  const narrow = useMedia("(max-width: 639px)");
  const admin = atLeast(space.role, "admin");
  const filtered = Boolean(sp.get("q") || sp.get("kind") || sp.get("entity") || sp.get("folder"));
  const changed = () => {
    sel.clear();
    list.reload();
    size.reload();
  };
  // Every match: in the background, its progress where the selection's bar was.
  const jobs = useJobs(space, TRASH_JOBS, (job) => {
    sayDone(job);
    list.reload();
    size.reload();
  });
  const started = (job: Job) => {
    jobs.follow(job);
    sel.clear();
  };
  const [restore, restoring] = useAction(async (items: TrashItems) => {
    if (items.filter) {
      started(await api.post<Job>(spaceApi(space, "/trash/restore"), items));
      return;
    }
    const r = await api.post<RestoreOut>(spaceApi(space, "/trash/restore"), items);
    if (r.restored.length > 0) {
      toast(t("trash.restored", { count: r.restored.length }));
    }
    setReport(r);
    changed();
  });
  const [purge, removing] = useAction(async (items: TrashItems) => {
    if (items.filter) {
      started(await api.post<Job>(spaceApi(space, "/trash/purge"), items));
      return;
    }
    const r = await api.post<Purged>(spaceApi(space, "/trash/purge"), items);
    toast(t("trash.purged", { count: r.purged.length, size: formatBytes(r.bytes) }));
    changed();
  });
  const env: Env = {
    space,
    selectable: canEdit(space),
    sel,
    shown: list.data
      ? { ids: list.data.data.map((r) => r.id), total: list.data.total ?? list.data.data.length, filtered }
      : undefined,
    filter,
    restore,
    purge: setPurging,
    busy: restoring || removing,
  };
  // What's selected acted on (or what's running), at the search's end; on its own line under it when narrow (beside
  // it, the search squeezed under it).
  const doing = jobs.running ? (
    <JobProgress job={jobs.running} />
  ) : sel.ids.length > 0 ? (
    <SelectionActions env={env} />
  ) : null;
  const beside = narrow ? null : doing;
  const under = narrow ? doing : null;
  return (
    <>
      <PageHeader
        title={t("trash.title")}
        note={size.data ? <Summary size={size.data} /> : null}
        more={t("trash.actions")}
        buttons={admin ? cleanupActions(setCleanup) : undefined}
      />
      {report ? <RestoreReport report={report} titles={titles} onClose={() => setReport(null)} /> : null}
      {/* Nothing to search in an empty trash. */}
      {filtered || (list.data?.total ?? 1) > 0 ? (
        <>
          <SearchBar space={space} rows={list.data?.data ?? []}>
            {beside}
          </SearchBar>
          {under ? <div class="mb-4 flex">{under}</div> : null}
        </>
      ) : null}
      <Async r={list}>{(page) => <Listing env={env} page={page} />}</Async>
      <ConfirmDialog
        open={purging !== null}
        onClose={() => setPurging(null)}
        title={t("trash.purgeTitle")}
        message={t("trash.purgeMessage", { count: purging?.count ?? 0 })}
        confirmLabel={t("trash.purge")}
        confirmIcon={<ShredIcon />}
        {...typeCount(purging?.count ?? 0)}
        onConfirm={() => purge(purging?.items ?? { ids: [] })}
      />
      <CleanupDialog space={space} mode={cleanup} onClose={() => setCleanup(null)} onDone={changed} />
    </>
  );
}
